import { useState } from 'react'
import { AlertCircle, Loader2, Upload } from 'lucide-react'
import { apiService } from '@/services/api'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { mensajeDeError } from '@/shared/lib/mensajeDeError'
import { CampoDeEvaluacion } from '@/features/rh/components/CampoDeEvaluacion'
import {
  ETIQUETA_EVALUACION_PENDIENTE,
  puedeSubirEvaluacion,
} from '@/features/rh/lib/evaluacionPendiente'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

/**
 * Aviso de la ficha para un candidato dado de alta en Seleccionado sin su
 * evaluación (SRH-309), con el modal para subirla.
 *
 * Mientras falte, el candidato está seleccionado a medias: no se le avisó, no
 * se avisó a RH y no sale en contratación. Al subirla se completa la
 * selección con sus efectos normales.
 */
export const EvaluacionPendienteDelCandidato = ({
  candidateId,
  nombre,
  permisos,
  onSubida,
}: {
  candidateId: string
  nombre: string
  permisos: string[] | undefined
  /** Se llama cuando el servidor ya completó la selección. */
  onSubida: () => void | Promise<void>
}) => {
  const [abierto, setAbierto] = useState(false)
  const [url, setUrl] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const puedeSubir = puedeSubirEvaluacion(permisos)

  const cerrar = () => {
    if (guardando) return
    setAbierto(false)
    setUrl('')
    setError(null)
  }

  const confirmar = async () => {
    if (!url) return
    setGuardando(true)
    setError(null)
    try {
      await apiService.subirEvaluacionDelCandidato(candidateId, url)
      setAbierto(false)
      setUrl('')
      await onSubida()
    } catch (fallo) {
      setError(mensajeDeError(fallo))
    } finally {
      setGuardando(false)
    }
  }

  return (
    <>
      <div className="mb-6 rounded-xl border border-[#F1D58A] bg-[#FEF6E2] p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-[#795E1B] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#795E1B]">
            {ETIQUETA_EVALUACION_PENDIENTE}
          </p>
          <p className="text-sm text-[#795E1B] mt-0.5">
            Se dio de alta como seleccionado sin su evaluación. Todavía no se le
            avisa que fue seleccionado, no se avisa a RH para contratarlo y no
            aparece en contratación.
          </p>
          {!puedeSubir && (
            <p className="text-xs text-[#795E1B] mt-1">
              La sube quien da de alta candidatos o cierra la selección.
            </p>
          )}
        </div>
        {puedeSubir && (
          <button
            type="button"
            onClick={() => setAbierto(true)}
            className={estiloDeBoton('primario', 'flex-shrink-0')}
          >
            <Upload className="w-4 h-4" />
            Subir evaluación
          </button>
        )}
      </div>

      {abierto && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900">Subir evaluación</h3>
            <p className="text-sm text-gray-600 mt-1">
              Al subirla, {nombre} queda seleccionado: se le avisa por correo y se
              avisa a RH para contratarlo.
            </p>

            <div className="mt-4">
              <CampoDeEvaluacion
                id="evaluacion-pendiente"
                url={url}
                onCambio={setUrl}
                deshabilitado={guardando}
              />
            </div>

            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={cerrar}
                disabled={guardando}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void confirmar()}
                disabled={!url || guardando}
                className={estiloDeBoton('primario')}
              >
                {guardando && <Loader2 className="w-4 h-4 animate-spin" />}
                {guardando ? 'Guardando…' : 'Confirmar selección'}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </>
  )
}
